import React, { useMemo, useState } from "react";
import { Check, ChevronLeft, ChevronRight } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  ASK_QUESTION_LIMITS,
  sanitizeAskQuestionAnswers,
  type AskQuestionItem,
} from "@optidev/shared-types";

/**
 * Ask-User-Question HITL card (optidev_ask_user_question).
 *
 * Clean + low-friction: one question renders as a compact card; 2-4 questions
 * render as a one-at-a-time stepper. An option with `allowsInput` (or the
 * always-present "Something else") reveals a text box nested INSIDE that option
 * as one unit (no doubled focus ring). Answers are sanitized client-side and
 * sent back through the standard onAction channel (encoded in the actionId,
 * like select_media), which unblocks the agent. Once answered (persisted in
 * clickedActions) it collapses to a one-line summary.
 */

const ACTION_PREFIX = "ask_question_answer:";
const OTHER = "__other__"; // synthetic free-text "Something else" choice

interface NormalizedOption {
  key: string;
  label: string;
  description?: string;
  allowsInput: boolean;
  dashed?: boolean;
}

interface AskQuestionCardProps {
  requestId: string;
  questions: AskQuestionItem[];
  summary?: string;
  messageId?: string;
  onAction?: (actionId: string, messageId: string) => void;
  clickedActions?: string[];
  isStale?: boolean;
}

interface SubmittedState {
  answers: Record<string, string[]>;
  skipped?: boolean;
}

/** Recover a prior submission from persisted clickedActions (refresh-safe). */
function findSubmitted(clickedActions: string[]): SubmittedState | null {
  const entry = clickedActions.find((a) => a.startsWith(ACTION_PREFIX));
  if (!entry) return null;
  try {
    const parsed = JSON.parse(entry.slice(ACTION_PREFIX.length));
    return { answers: parsed.answers ?? {}, skipped: parsed.skipped };
  } catch {
    return { answers: {} };
  }
}

function normalizeOptions(q: AskQuestionItem): NormalizedOption[] {
  return [
    ...q.options.map((o) => ({
      key: o.label,
      label: o.label,
      description: o.description,
      allowsInput: !!o.allowsInput,
    })),
    { key: OTHER, label: "Something else", allowsInput: true, dashed: true },
  ];
}

export function AskQuestionCard({
  requestId,
  questions,
  summary,
  messageId,
  onAction,
  clickedActions = [],
  isStale = false,
}: AskQuestionCardProps) {
  const submitted = useMemo(() => findSubmitted(clickedActions), [clickedActions]);

  const [step, setStep] = useState(0);
  const [selections, setSelections] = useState<Record<string, string[]>>({});
  // free-text per (question header + option key)
  const [inputs, setInputs] = useState<Record<string, string>>({});

  // ---- Answered: collapse to a clean receipt-style summary ----
  if (submitted) {
    const entries = Object.entries(submitted.answers);
    const skipped = submitted.skipped || entries.length === 0;
    return (
      <Card className="border border-border bg-muted/20 px-3.5 py-3">
        <div className="border-b border-border pb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          {skipped ? "Skipped" : "Your answers"}
        </div>
        {!skipped && (
          <dl className="divide-y divide-border">
            {entries.map(([header, values]) => (
              <div key={header} className="py-2.5">
                <dt className="text-xs text-muted-foreground">{header}</dt>
                <dd className="mt-1 text-sm font-medium text-foreground">
                  {values.join(", ")}
                </dd>
              </div>
            ))}
          </dl>
        )}
      </Card>
    );
  }

  const multi = questions.length > 1;
  const q = questions[step];
  const options = normalizeOptions(q);
  const selected = selections[q.header] ?? [];
  const ikey = (key: string) => `${q.header}::${key}`;
  const disabled = isStale;

  const toggle = (key: string) => {
    setSelections((prev) => {
      const cur = prev[q.header] ?? [];
      if (q.multiSelect) {
        return {
          ...prev,
          [q.header]: cur.includes(key)
            ? cur.filter((k) => k !== key)
            : [...cur, key],
        };
      }
      return { ...prev, [q.header]: cur.includes(key) ? [] : [key] };
    });
  };

  // A step is answered if a plain option is picked, or an input-bearing option
  // is picked AND has text.
  const stepAnswered = selected.some((key) => {
    const opt = options.find((o) => o.key === key);
    if (!opt) return false;
    return !opt.allowsInput || (inputs[ikey(key)] ?? "").trim().length > 0;
  });

  const buildAnswersFrom = (
    sel: Record<string, string[]>,
  ): Record<string, string[]> => {
    const out: Record<string, string[]> = {};
    for (const item of questions) {
      const opts = normalizeOptions(item);
      const picks = sel[item.header] ?? [];
      const vals: string[] = [];
      for (const key of picks) {
        const opt = opts.find((o) => o.key === key);
        if (!opt) continue;
        if (opt.allowsInput) {
          const txt = (inputs[`${item.header}::${key}`] ?? "").trim();
          if (txt) vals.push(txt);
        } else {
          vals.push(opt.label);
        }
      }
      if (vals.length) out[item.header] = vals;
    }
    // Sanitize client-side too (server re-sanitizes authoritatively).
    return sanitizeAskQuestionAnswers(out);
  };

  const send = (payload: SubmittedState) => {
    onAction?.(ACTION_PREFIX + JSON.stringify({ requestId, ...payload }), messageId ?? "");
  };

  const finishWith = (sel: Record<string, string[]>) => {
    const answers = buildAnswersFrom(sel);
    send({ answers, skipped: Object.keys(answers).length === 0 });
  };

  const isLast = step === questions.length - 1;

  // "Next/Continue" — requires the current question to be answered.
  const onNext = () => {
    if (isLast) finishWith(selections);
    else setStep((s) => s + 1);
  };

  // "Skip" — skip THIS question only: drop any selection for it and advance.
  // Skipping every question just yields no answers (treated as fully skipped).
  const onSkipQuestion = () => {
    const sel = { ...selections };
    delete sel[q.header];
    setSelections(sel);
    if (isLast) finishWith(sel);
    else setStep((s) => s + 1);
  };

  return (
    <Card
      className={cn(
        "border-border/70 bg-card/95 p-4 shadow-sm",
        disabled && "pointer-events-none opacity-60",
      )}
    >
      {/* Header */}
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="text-sm font-semibold text-foreground">
          {summary?.trim() || "A couple of quick questions"}
        </div>
        {multi && (
          <div className="flex items-center gap-1.5">
            {questions.map((_, i) => (
              <span
                key={i}
                className={cn(
                  "h-1.5 rounded-full transition-all",
                  i === step ? "w-4 bg-primary" : "w-1.5",
                  i < step ? "bg-primary/50" : i === step ? "" : "bg-muted-foreground/25",
                )}
              />
            ))}
            <span className="ml-1 text-xs tabular-nums text-muted-foreground">
              {step + 1}/{questions.length}
            </span>
          </div>
        )}
      </div>

      {/* Question */}
      <p className="mb-3 text-[15px] font-medium leading-snug text-foreground">
        {q.question}
      </p>

      {/* Options */}
      <div className="flex flex-col gap-2">
        {options.map((opt) => {
          const active = selected.includes(opt.key);
          return (
            <div
              key={opt.key}
              className={cn(
                "overflow-hidden rounded-xl border transition-colors",
                active
                  ? "border-primary bg-primary/[0.07]"
                  : cn(
                      "border-border hover:border-primary/40 hover:bg-muted/40",
                      opt.dashed && "border-dashed",
                    ),
              )}
            >
              <button
                type="button"
                onClick={() => toggle(opt.key)}
                className="flex w-full items-start gap-3 px-3 py-2.5 text-left"
              >
                <span
                  className={cn(
                    "mt-0.5 flex h-[18px] w-[18px] shrink-0 items-center justify-center border transition-colors",
                    q.multiSelect ? "rounded-md" : "rounded-full",
                    active
                      ? "border-primary bg-primary text-primary-foreground"
                      : "border-muted-foreground/40",
                  )}
                >
                  {active && <Check className="h-3 w-3" strokeWidth={3} />}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium text-foreground">{opt.label}</span>
                  {active && opt.description && (
                    <span className="mt-0.5 block text-xs leading-snug text-muted-foreground">
                      {opt.description}
                    </span>
                  )}
                </span>
              </button>

              {/* Nested free-text input — a borderless line within the option,
                  so it reads as one unit (no box-in-box, no second ring). */}
              {opt.allowsInput && active && (
                <div className="border-t border-primary/20 px-3 py-2.5">
                  <input
                    autoFocus
                    value={inputs[ikey(opt.key)] ?? ""}
                    maxLength={ASK_QUESTION_LIMITS.maxValueLength}
                    onChange={(e) =>
                      setInputs((p) => ({ ...p, [ikey(opt.key)]: e.target.value }))
                    }
                    onClick={(e) => e.stopPropagation()}
                    placeholder={opt.key === OTHER ? "Type your answer…" : "Type here…"}
                    className="w-full bg-transparent text-sm text-foreground placeholder:text-muted-foreground/70 focus:outline-none"
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div className="mt-4 flex items-center justify-between">
        <div>
          {multi && step > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="text-muted-foreground hover:text-foreground"
              onClick={() => setStep((s) => s - 1)}
            >
              <ChevronLeft className="mr-1 h-4 w-4" />
              Back
            </Button>
          )}
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            className="text-muted-foreground hover:text-foreground"
            onClick={onSkipQuestion}
          >
            {isLast ? "Skip" : "Skip question"}
          </Button>
          <Button size="sm" onClick={onNext} disabled={!stepAnswered}>
            {isLast ? "Continue" : "Next"}
            {!isLast && <ChevronRight className="ml-1 h-4 w-4" />}
          </Button>
        </div>
      </div>
    </Card>
  );
}
